import { useState, useEffect, useCallback } from 'react';
import {
  FiPlus, FiEdit, FiTrash2, FiPackage, FiSearch, FiX,
  FiUpload, FiStar, FiTrendingUp, FiZap, FiEye, FiEyeOff,
  FiCheck, FiAlertTriangle, FiRefreshCw, FiFilter, FiCheckCircle,
  FiArrowLeft, FiArrowRight, FiMaximize2, FiImage, FiSmartphone,
  FiMonitor, FiLayers, FiInfo, FiHeart, FiShoppingCart
} from 'react-icons/fi';
import api from '../../utils/api';
import { toast } from 'react-toastify';
import AdminSidebar from '../../components/AdminSidebar';
import { optimizeImage } from '../../utils/imageOptimizer';
import '../../styles/Admin.css';
import '../../styles/AdminForms.css';

// Currency formatter
const pesoFormatter = new Intl.NumberFormat('en-PH', {
  style: 'currency',
  currency: 'PHP',
});

const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [summary, setSummary] = useState({
    totalProducts: 0,
    inStock: 0,
    lowStock: 0,
    outOfStock: 0,
    published: 0,
    unpublished: 0,
    lowStockAlerts: []
  });

  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [saving, setSaving] = useState(false);

  // Media Manager & Live Preview State
  const [mediaViewMode, setMediaViewMode] = useState('manager'); // 'manager' | 'preview'
  const [previewDevice, setPreviewDevice] = useState('desktop'); // 'desktop' | 'mobile'
  const [previewZoomImg, setPreviewZoomImg] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  // Filters State
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [inventoryStatusFilter, setInventoryStatusFilter] = useState(''); // in_stock, low_stock, out_of_stock
  const [visibilityFilter, setVisibilityFilter] = useState(''); // true, false
  const [merchandisingFilter, setMerchandisingFilter] = useState(''); // featured, bestSeller, newArrival
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 10;

  // Form State
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    originalPrice: '',
    category: 'Lip Tint',
    brand: 'AmaraCé',
    stock: '0',
    lowStockThreshold: '10',
    sku: '',
    featured: false,
    bestSeller: false,
    newArrival: false,
    published: true,
    ingredients: '',
    howToUse: ''
  });

  const [productImages, setProductImages] = useState([]);
  const [images, setImages] = useState([]);

  // Fetch Inventory Summary
  const fetchSummary = useCallback(async () => {
    try {
      const { data } = await api.get('/products/inventory/summary');
      setSummary(data);
    } catch (err) {
      console.error('Error fetching inventory summary:', err);
    }
  }, []);

  // Fetch Products with Filters
  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage,
        limit,
        adminView: 'true'
      });

      if (searchQuery.trim()) params.append('search', searchQuery.trim());
      if (selectedCategory) params.append('category', selectedCategory);
      if (inventoryStatusFilter) params.append('inventoryStatus', inventoryStatusFilter);
      if (visibilityFilter) params.append('published', visibilityFilter);
      if (merchandisingFilter) params.append('merchandising', merchandisingFilter);

      const [prodRes, catRes] = await Promise.all([
        api.get(`/products?${params.toString()}`),
        api.get('/products/categories/all')
      ]);

      setProducts(prodRes.data.products || []);
      setTotalPages(prodRes.data.totalPages || 1);
      setCategories(catRes.data || []);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Failed to fetch products');
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchQuery, selectedCategory, inventoryStatusFilter, visibilityFilter, merchandisingFilter]);

  useEffect(() => {
    fetchProducts();
    fetchSummary();
  }, [fetchProducts, fetchSummary]);

  // Handle Drag & Drop
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (e.dataTransfer && e.dataTransfer.files) {
      processSelectedFiles(Array.from(e.dataTransfer.files));
    }
  };

  const processSelectedFiles = (files) => {
    const validImageFiles = files.filter(f => f.type.startsWith('image/'));
    if (validImageFiles.length === 0) {
      return toast.error('Please select valid image files (JPG, PNG, WebP)');
    }

    if (productImages.length + validImageFiles.length > 10) {
      toast.warning('Maximum 10 product images allowed. Excess images were omitted.');
    }

    const availableSlots = Math.max(0, 10 - productImages.length);
    const filesToAdd = validImageFiles.slice(0, availableSlots);

    const oversizedFiles = filesToAdd.filter(f => f.size > 50 * 1024 * 1024);
    if (oversizedFiles.length > 0) {
      return toast.error('Each image must be less than 50MB');
    }

    setImages(prev => [...prev, ...filesToAdd]);

    const newPreviews = filesToAdd.map(file => ({
      url: URL.createObjectURL(file),
      file: file,
      isNew: true
    }));

    setProductImages(prev => [...prev, ...newPreviews]);
  };

  // Handle File Input Selection
  const handleImageUpload = (e) => {
    if (e.target.files) {
      processSelectedFiles(Array.from(e.target.files));
    }
  };

  // Detect Aspect Ratio and Dimensions
  const handleImageLoaded = (index, e) => {
    const { naturalWidth, naturalHeight } = e.target;
    if (naturalWidth && naturalHeight) {
      const ratioVal = naturalWidth / naturalHeight;
      let ratioLabel = '1:1 Square';
      if (ratioVal < 0.75) ratioLabel = '9:16 Portrait';
      else if (ratioVal < 0.9) ratioLabel = '4:5 Portrait';
      else if (ratioVal < 1.1) ratioLabel = '1:1 Square';
      else if (ratioVal < 1.5) ratioLabel = '4:3 Landscape';
      else ratioLabel = '16:9 Landscape';

      setProductImages(prev => {
        const copy = [...prev];
        if (copy[index]) {
          copy[index] = {
            ...copy[index],
            ratio: ratioLabel,
            dims: `${naturalWidth}×${naturalHeight}`
          };
        }
        return copy;
      });
    }
  };

  const removeImage = (index) => {
    const imageToRemove = productImages[index];
    const newProductImages = productImages.filter((_, i) => i !== index);
    setProductImages(newProductImages);

    if (imageToRemove?.isNew) {
      const newImages = images.filter(file => file !== imageToRemove.file);
      setImages(newImages);
    }
  };

  const moveImage = (index, direction) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= productImages.length) return;

    setProductImages(prev => {
      const copy = [...prev];
      const temp = copy[index];
      copy[index] = copy[targetIndex];
      copy[targetIndex] = temp;
      return copy;
    });
  };

  const makePrimary = (index) => {
    if (index <= 0 || index >= productImages.length) return;
    setProductImages(prev => {
      const copy = [...prev];
      const [item] = copy.splice(index, 1);
      copy.unshift(item);
      return copy;
    });
    toast.info('Primary cover image updated');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: checked }));
  };

  // Open Modal for Add
  const openAdd = () => {
    setEditProduct(null);
    setMediaViewMode('manager');
    setPreviewZoomImg(null);
    setFormData({
      name: '',
      description: '',
      price: '',
      originalPrice: '',
      category: categories[0] || 'Lip Tint',
      brand: 'AmaraCé',
      stock: '15',
      lowStockThreshold: '10',
      sku: '',
      featured: false,
      bestSeller: false,
      newArrival: false,
      published: true,
      ingredients: '',
      howToUse: ''
    });
    setProductImages([]);
    setImages([]);
    setShowModal(true);
  };

  // Open Modal for Edit
  const openEdit = (product) => {
    setEditProduct(product);
    setMediaViewMode('manager');
    setPreviewZoomImg(null);
    setFormData({
      name: product.name || '',
      description: product.description || '',
      price: String(product.price || ''),
      originalPrice: product.originalPrice ? String(product.originalPrice) : '',
      category: product.category || 'Lip Tint',
      brand: product.brand || 'AmaraCé',
      stock: String(product.stock !== undefined ? product.stock : 0),
      lowStockThreshold: String(product.lowStockThreshold !== undefined ? product.lowStockThreshold : 10),
      sku: product.sku || '',
      featured: Boolean(product.featured),
      bestSeller: Boolean(product.bestSeller),
      newArrival: Boolean(product.newArrival),
      published: product.published !== false,
      ingredients: product.ingredients || '',
      howToUse: product.howToUse || ''
    });

    const existingImages = (product.images || []).map(url => ({
      url,
      isNew: false
    }));
    setProductImages(existingImages);
    setImages([]);
    setShowModal(true);
  };

  // Save Product (Create or Update)
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) return toast.error('Product name is required');
    if (!formData.price) return toast.error('Product price is required');

    setSaving(true);
    const data = new FormData();
    data.append('name', formData.name.trim());
    data.append('description', formData.description);
    data.append('price', formData.price);
    if (formData.originalPrice) data.append('originalPrice', formData.originalPrice);
    data.append('category', formData.category);
    data.append('brand', formData.brand || 'AmaraCé');
    data.append('stock', formData.stock || '0');
    data.append('lowStockThreshold', formData.lowStockThreshold || '10');
    data.append('sku', formData.sku ? formData.sku.trim().toUpperCase() : '');
    data.append('featured', formData.featured ? 'true' : 'false');
    data.append('bestSeller', formData.bestSeller ? 'true' : 'false');
    data.append('newArrival', formData.newArrival ? 'true' : 'false');
    data.append('published', formData.published ? 'true' : 'false');
    data.append('ingredients', formData.ingredients || '');
    data.append('howToUse', formData.howToUse || '');

    // Construct imageMap preserving exact sequence
    const imageMap = productImages.map(img => {
      if (!img.isNew) {
        return { type: 'existing', url: img.url };
      } else {
        const fileIdx = images.indexOf(img.file);
        return { type: 'new', index: fileIdx >= 0 ? fileIdx : 0 };
      }
    });
    data.append('imageMap', JSON.stringify(imageMap));

    const existingImageUrls = productImages.filter(img => !img.isNew).map(img => img.url);
    data.append('existingImages', JSON.stringify(existingImageUrls));

    images.forEach(image => {
      data.append('images', image);
    });

    try {
      if (editProduct) {
        await api.put(`/products/${editProduct._id}`, data, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Product updated successfully!');
      } else {
        await api.post('/products', data, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Product added successfully!');
      }

      setShowModal(false);
      setEditProduct(null);
      fetchProducts();
      fetchSummary();
    } catch (error) {
      console.error('Error saving product:', error);
      toast.error(error.response?.data?.message || 'Failed to save product');
    } finally {
      setSaving(false);
    }
  };

  // Delete Product
  const handleDelete = async (id, name) => {
    if (window.confirm(`Are you sure you want to delete "${name}"? This action cannot be undone.`)) {
      try {
        await api.delete(`/products/${id}`);
        toast.success('Product removed successfully');
        fetchProducts();
        fetchSummary();
      } catch (error) {
        console.error('Error deleting product:', error);
        toast.error('Failed to delete product');
      }
    }
  };

  // Helper for Inventory Status calculation
  const getInventoryStatus = (stock, threshold = 10) => {
    const s = Number(stock) || 0;
    const t = Number(threshold) || 10;
    if (s === 0) return { label: 'Out of Stock', class: 'out-of-stock', dot: '🔴' };
    if (s <= t) return { label: `Low Stock (${s} left)`, class: 'low-stock', dot: '🟡' };
    return { label: 'In Stock', class: 'in-stock', dot: '🟢' };
  };

  const hasActiveFilters = Boolean(
    searchQuery || selectedCategory || inventoryStatusFilter || visibilityFilter || merchandisingFilter
  );

  const clearAllFilters = () => {
    setSearchQuery('');
    setSelectedCategory('');
    setInventoryStatusFilter('');
    setVisibilityFilter('');
    setMerchandisingFilter('');
    setCurrentPage(1);
  };

  return (
    <div className="admin-layout">
      <AdminSidebar />

      <main className="admin-main">
        {/* Page Header */}
        <div className="admin-header-row">
          <div>
            <h1>Product Inventory & Catalog</h1>
            <p className="admin-subtitle">
              Manage stock levels, SKUs, visibility, merchandising tags, and formula details
            </p>
          </div>

          <button type="button" className="btn-add-primary" onClick={openAdd}>
            <FiPlus /> Add New Product
          </button>
        </div>

        {/* ── Inventory Summary Dashboard Cards ──────────── */}
        <div className="inventory-summary-grid">
          <div
            className={`inventory-metric-card ${!inventoryStatusFilter && !visibilityFilter ? 'active' : ''}`}
            onClick={clearAllFilters}
          >
            <div className="metric-icon total"><FiPackage /></div>
            <div className="metric-content">
              <span className="metric-label">Total Catalog</span>
              <h3 className="metric-val">{summary.totalProducts}</h3>
              <span className="metric-sub">{summary.published} Published • {summary.unpublished} Drafts</span>
            </div>
          </div>

          <div
            className={`inventory-metric-card ${inventoryStatusFilter === 'in_stock' ? 'active' : ''}`}
            onClick={() => { setInventoryStatusFilter('in_stock'); setCurrentPage(1); }}
          >
            <div className="metric-icon in-stock"><FiCheckCircle /></div>
            <div className="metric-content">
              <span className="metric-label">In Stock</span>
              <h3 className="metric-val in-stock">{summary.inStock}</h3>
              <span className="metric-sub">Healthy inventory levels</span>
            </div>
          </div>

          <div
            className={`inventory-metric-card alert ${inventoryStatusFilter === 'low_stock' ? 'active' : ''}`}
            onClick={() => { setInventoryStatusFilter('low_stock'); setCurrentPage(1); }}
          >
            <div className="metric-icon low-stock"><FiAlertTriangle /></div>
            <div className="metric-content">
              <div className="metric-label-row">
                <span className="metric-label">Low Stock Alert</span>
                {summary.lowStock > 0 && <span className="alert-badge">{summary.lowStock} Needs Attention</span>}
              </div>
              <h3 className="metric-val low-stock">{summary.lowStock}</h3>
              <span className="metric-sub">At or below alert threshold</span>
            </div>
          </div>

          <div
            className={`inventory-metric-card danger ${inventoryStatusFilter === 'out_of_stock' ? 'active' : ''}`}
            onClick={() => { setInventoryStatusFilter('out_of_stock'); setCurrentPage(1); }}
          >
            <div className="metric-icon out-of-stock"><FiX /></div>
            <div className="metric-content">
              <span className="metric-label">Out of Stock</span>
              <h3 className="metric-val out-of-stock">{summary.outOfStock}</h3>
              <span className="metric-sub">Marked as Sold Out in store</span>
            </div>
          </div>
        </div>

        {/* ── Inventory Filters Toolbar ──────────────────── */}
        <div className="admin-filter-toolbar">
          <div className="filter-search-box">
            <FiSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search by Product Name, SKU, or Keyword..."
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
            />
            {searchQuery && (
              <button
                type="button"
                className="clear-icon-btn"
                onClick={() => setSearchQuery('')}
              >
                <FiX size={14} />
              </button>
            )}
          </div>

          <div className="filter-dropdowns-row">
            {/* Inventory Status */}
            <select
              value={inventoryStatusFilter}
              onChange={(e) => { setInventoryStatusFilter(e.target.value); setCurrentPage(1); }}
              aria-label="Filter by inventory status"
            >
              <option value="">Inventory: All Levels</option>
              <option value="in_stock">🟢 In Stock (Above Threshold)</option>
              <option value="low_stock">🟡 Low Stock (Needs Attention)</option>
              <option value="out_of_stock">🔴 Out of Stock (0 items)</option>
            </select>

            {/* Visibility Filter */}
            <select
              value={visibilityFilter}
              onChange={(e) => { setVisibilityFilter(e.target.value); setCurrentPage(1); }}
              aria-label="Filter by visibility"
            >
              <option value="">Visibility: All</option>
              <option value="true">Published (Live in Store)</option>
              <option value="false">Draft / Unpublished (Hidden)</option>
            </select>

            {/* Merchandising Filter */}
            <select
              value={merchandisingFilter}
              onChange={(e) => { setMerchandisingFilter(e.target.value); setCurrentPage(1); }}
              aria-label="Filter by merchandising"
            >
              <option value="">Merchandising: All</option>
              <option value="featured">★ Featured</option>
              <option value="bestSeller">🏆 Best Seller</option>
              <option value="newArrival">✨ New Arrival</option>
            </select>

            {/* Category Filter */}
            <select
              value={selectedCategory}
              onChange={(e) => { setSelectedCategory(e.target.value); setCurrentPage(1); }}
              aria-label="Filter by category"
            >
              <option value="">Category: All</option>
              {categories.map((cat) => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>

            {hasActiveFilters && (
              <button
                type="button"
                className="btn-reset-filters"
                onClick={clearAllFilters}
              >
                Reset Filters
              </button>
            )}
          </div>
        </div>

        {/* ── Products Table ─────────────────────────────── */}
        <div className="admin-table-container">
          <table className="admin-table products-admin-table">
            <thead>
              <tr>
                <th>Product</th>
                <th>SKU</th>
                <th>Category</th>
                <th>Price</th>
                <th>Inventory</th>
                <th>Status</th>
                <th>Visibility</th>
                <th>Merchandising</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="9" style={{ textAlign: 'center', padding: '40px' }}>
                    <FiRefreshCw className="spin" size={24} />
                    <p style={{ marginTop: '8px', color: '#666' }}>Loading catalog...</p>
                  </td>
                </tr>
              ) : products.length > 0 ? (
                products.map(product => {
                  const invStatus = getInventoryStatus(product.stock, product.lowStockThreshold);
                  const isPublished = product.published !== false;

                  return (
                    <tr key={product._id}>
                      {/* Product Thumbnail & Name */}
                      <td className="product-info-cell">
                        <img
                          src={optimizeImage(product.images?.[0] || '/placeholder.jpg', 80)}
                          alt=""
                          className="product-thumb-small"
                        />
                        <div className="product-meta">
                          <strong className="product-name">{product.name}</strong>
                          <span className="product-brand-sub">{product.brand || 'AmaraCé'}</span>
                        </div>
                      </td>

                      {/* SKU */}
                      <td>
                        <code className="sku-badge">{product.sku || '—'}</code>
                      </td>

                      {/* Category */}
                      <td>
                        <span className="category-pill">{product.category}</span>
                      </td>

                      {/* Price */}
                      <td className="price-cell">
                        <strong>₱{product.price?.toFixed(2)}</strong>
                        {product.originalPrice && product.originalPrice > product.price && (
                          <span className="original-price-sub">₱{product.originalPrice?.toFixed(2)}</span>
                        )}
                      </td>

                      {/* Inventory Count */}
                      <td className="inventory-qty-cell">
                        <div className="inventory-qty-row">
                          <span className={`inventory-dot ${invStatus.class}`} />
                          <span className="inventory-num">{product.stock}</span>
                          <span className="inventory-unit">units</span>
                        </div>
                        <small className="threshold-sub">Alert: ≤{product.lowStockThreshold || 10}</small>
                      </td>

                      {/* Inventory Status Pill */}
                      <td>
                        <span className={`status-pill ${invStatus.class}`}>
                          {invStatus.label}
                        </span>
                      </td>

                      {/* Visibility Pill */}
                      <td>
                        <span className={`visibility-pill ${isPublished ? 'published' : 'draft'}`}>
                          {isPublished ? <><FiEye size={12} /> Published</> : <><FiEyeOff size={12} /> Draft</>}
                        </span>
                      </td>

                      {/* Merchandising Badges */}
                      <td>
                        <div className="merchandising-tags-row">
                          {product.featured && <span className="merch-tag featured">Featured</span>}
                          {product.bestSeller && <span className="merch-tag bestseller">Best Seller</span>}
                          {product.newArrival && <span className="merch-tag new">New</span>}
                          {!product.featured && !product.bestSeller && !product.newArrival && (
                            <span className="merch-tag none">Standard</span>
                          )}
                        </div>
                      </td>

                      {/* Actions */}
                      <td style={{ textAlign: 'right' }}>
                        <div className="action-btns-heavy">
                          <button
                            type="button"
                            className="edit-btn-v2"
                            onClick={() => openEdit(product)}
                            title="Edit Product"
                          >
                            <FiEdit /> <span>Edit</span>
                          </button>
                          <button
                            type="button"
                            className="delete-btn-v2"
                            onClick={() => handleDelete(product._id, product.name)}
                            title="Delete Product"
                          >
                            <FiTrash2 /> <span>Delete</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="9" style={{ textAlign: 'center', padding: '60px 20px', color: '#666' }}>
                    <FiPackage size={40} style={{ opacity: 0.3, marginBottom: '12px' }} />
                    <h4>No products found</h4>
                    <p>Try adjusting your search query, inventory level, or category filter.</p>
                    {hasActiveFilters && (
                      <button
                        type="button"
                        className="btn-reset-filters"
                        onClick={clearAllFilters}
                        style={{ marginTop: '12px' }}
                      >
                        Clear All Filters
                      </button>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* ── Pagination ─────────────────────────────── */}
        {!loading && totalPages > 1 && (
          <div className="pagination">
            <button
              type="button"
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage <= 1}
              className="page-nav-btn"
            >
              Previous
            </button>

            <div className="page-numbers" style={{ display: 'flex', gap: '0.5rem' }}>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                <button
                  key={page}
                  type="button"
                  onClick={() => setCurrentPage(page)}
                  className={`page-btn ${currentPage === page ? 'active' : ''}`}
                >
                  {page}
                </button>
              ))}
            </div>

            <button
              type="button"
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage >= totalPages}
              className="page-nav-btn"
            >
              Next
            </button>
          </div>
        )}

        {/* ── Product Modal (Add / Edit) ─────────────── */}
        {showModal && (
          <div className="modal-overlay" onClick={() => setShowModal(false)}>
            <div className="modal redesigned-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <div>
                  <h2>{editProduct ? `Edit: ${editProduct.name}` : 'Create New Product'}</h2>
                  <p className="modal-subtitle">Configure pricing, stock threshold, merchandising, and formula</p>
                </div>
                <button
                  type="button"
                  className="close-modal-btn"
                  onClick={() => { setShowModal(false); setEditProduct(null); }}
                  aria-label="Close modal"
                >
                  <FiX size={20} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="redesigned-form">
                {/* ── SECTION 1: PRODUCT MEDIA & LIVE PREVIEW ── */}
                <div className="form-section admin-media-section">
                  <div className="section-header-with-tabs">
                    <div>
                      <h3 className="section-title">1. Product Media & Adaptive Studio Stage</h3>
                      <p className="section-description">
                        Upload up to 10 product images. AmaraCé uses an adaptive ivory stage (4:5) to showcase your complete packaging and bottles without cropping.
                      </p>
                    </div>

                    <div className="media-mode-toggle-pill">
                      <button
                        type="button"
                        className={`mode-pill-btn ${mediaViewMode === 'manager' ? 'active' : ''}`}
                        onClick={() => setMediaViewMode('manager')}
                      >
                        <FiImage size={14} /> Media Manager ({productImages.length}/10)
                      </button>
                      <button
                        type="button"
                        className={`mode-pill-btn ${mediaViewMode === 'preview' ? 'active' : ''}`}
                        onClick={() => setMediaViewMode('preview')}
                      >
                        <FiEye size={14} /> Live Storefront Preview
                      </button>
                    </div>
                  </div>

                  {mediaViewMode === 'manager' ? (
                    <div className="admin-media-manager-body">
                      {/* Drag & Drop Zone */}
                      <div
                        className={`admin-dropzone-box ${isDragging ? 'is-dragging' : ''}`}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                      >
                        <input
                          type="file"
                          id="product-media-upload-input"
                          multiple
                          accept="image/png,image/jpeg,image/jpg,image/webp"
                          onChange={handleImageUpload}
                          style={{ display: 'none' }}
                        />
                        <label htmlFor="product-media-upload-input" className="dropzone-inner-content">
                          <div className="dropzone-icon-circle">
                            <FiUpload size={22} />
                          </div>
                          <div className="dropzone-text-group">
                            <strong>Drop product images here, or <span className="browse-link">browse files</span></strong>
                            <small>Supports PNG, JPG, WebP up to 50MB (Adaptive ivory stage preserves complete bottle & details)</small>
                          </div>
                          <div className="dropzone-count-pill">
                            <span>{productImages.length} of 10</span>
                          </div>
                        </label>
                      </div>

                      {/* Image Preview Grid */}
                      {productImages.length > 0 ? (
                        <div className="admin-media-cards-grid">
                          {productImages.map((image, index) => (
                            <div key={index} className={`admin-media-card ${index === 0 ? 'is-primary' : index === 1 ? 'is-secondary' : ''}`}>
                              {/* Stage Frame */}
                              <div className="admin-media-stage">
                                <img
                                  src={optimizeImage(image.url, 400)}
                                  alt={`Upload ${index + 1}`}
                                  onLoad={(e) => handleImageLoaded(index, e)}
                                />

                                {/* Badges */}
                                <div className="admin-media-badges">
                                  {index === 0 ? (
                                    <span className="admin-badge-role primary">
                                      <FiStar size={10} /> Primary Cover
                                    </span>
                                  ) : index === 1 ? (
                                    <span className="admin-badge-role secondary">
                                      <FiEye size={10} /> Hover View
                                    </span>
                                  ) : (
                                    <span className="admin-badge-role position">#{index + 1}</span>
                                  )}

                                  {image.ratio && (
                                    <span className="admin-badge-ratio" title={image.dims || ''}>
                                      {image.ratio}
                                    </span>
                                  )}
                                </div>

                                {/* Floating Quick Zoom */}
                                <button
                                  type="button"
                                  className="admin-stage-zoom-btn"
                                  onClick={() => setPreviewZoomImg(image.url)}
                                  title="Inspect full image"
                                >
                                  <FiMaximize2 size={13} />
                                </button>
                              </div>

                              {/* Card Toolbar */}
                              <div className="admin-media-toolbar">
                                <div className="reorder-btn-group">
                                  <button
                                    type="button"
                                    className="media-tool-btn"
                                    onClick={() => moveImage(index, -1)}
                                    disabled={index === 0}
                                    title="Move left"
                                  >
                                    <FiArrowLeft size={13} />
                                  </button>
                                  <button
                                    type="button"
                                    className="media-tool-btn"
                                    onClick={() => moveImage(index, 1)}
                                    disabled={index === productImages.length - 1}
                                    title="Move right"
                                  >
                                    <FiArrowRight size={13} />
                                  </button>
                                  {index > 0 && (
                                    <button
                                      type="button"
                                      className="media-tool-btn make-primary-btn"
                                      onClick={() => makePrimary(index)}
                                      title="Set as Cover Image"
                                    >
                                      <FiStar size={12} /> Set Primary
                                    </button>
                                  )}
                                </div>

                                <button
                                  type="button"
                                  className="media-tool-btn delete-btn"
                                  onClick={() => removeImage(index)}
                                  title="Delete image"
                                >
                                  <FiTrash2 size={13} />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="admin-media-empty-notice">
                          <p>No media added yet. Upload high-quality product photography to display on the storefront.</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    /* ── LIVE STOREFRONT PREVIEW TAB ── */
                    <div className="admin-storefront-live-preview">
                      <div className="preview-toolbar-header">
                        <div className="preview-status-pill">
                          <span className="live-dot"></span> Live Storefront Simulation
                        </div>
                        <div className="device-switcher-pill">
                          <button
                            type="button"
                            className={`device-btn ${previewDevice === 'desktop' ? 'active' : ''}`}
                            onClick={() => setPreviewDevice('desktop')}
                          >
                            <FiMonitor size={14} /> Desktop Grid
                          </button>
                          <button
                            type="button"
                            className={`device-btn ${previewDevice === 'mobile' ? 'active' : ''}`}
                            onClick={() => setPreviewDevice('mobile')}
                          >
                            <FiSmartphone size={14} /> Mobile View
                          </button>
                        </div>
                      </div>

                      <div className="preview-simulation-arena">
                        {/* Simulated Storefront Card */}
                        <div className={`storefront-card-preview-shell device-${previewDevice}`}>
                          <div className="preview-card-frame">
                            <div className="preview-card-stage">
                              <div className="preview-stage-canvas">
                                <img
                                  src={optimizeImage(productImages[0]?.url || '/placeholder.jpg', 600)}
                                  alt="Primary view"
                                  className="preview-primary-img"
                                />
                                {productImages[1] && (
                                  <img
                                    src={optimizeImage(productImages[1].url, 600)}
                                    alt="Hover view"
                                    className="preview-secondary-img"
                                  />
                                )}
                              </div>

                              {/* Badges */}
                              <div className="preview-badges-layer">
                                {Number(formData.stock) === 0 ? (
                                  <span className="preview-badge sold-out">Sold Out</span>
                                ) : formData.bestSeller ? (
                                  <span className="preview-badge best-seller">★ Best Seller</span>
                                ) : formData.newArrival ? (
                                  <span className="preview-badge new">New Arrival</span>
                                ) : null}
                              </div>

                              {/* Action buttons */}
                              <div className="preview-top-actions">
                                <span className="preview-action-icon"><FiHeart size={14} /></span>
                                <span className="preview-action-icon"><FiEye size={14} /></span>
                              </div>

                              <div className="preview-quick-add-bar">
                                <span><FiShoppingCart size={13} /> {Number(formData.stock) === 0 ? 'Sold Out' : 'Quick Add'}</span>
                              </div>
                            </div>

                            <div className="preview-card-meta">
                              <span className="preview-cat-text">{formData.category || 'Collection'}</span>
                              <h4 className="preview-title-text">{formData.name || 'Product Title Preview'}</h4>
                              <div className="preview-price-row">
                                <span className="preview-price-val">₱{Number(formData.price || 0).toFixed(2)}</span>
                                {formData.originalPrice && Number(formData.originalPrice) > Number(formData.price) && (
                                  <span className="preview-old-price">₱{Number(formData.originalPrice).toFixed(2)}</span>
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="preview-guidance-card">
                            <h5>💡 Live Visual Verification:</h5>
                            <ul>
                              <li><strong>Zero Cropping:</strong> Your product is framed in a warm ivory stage (`#FAF8F5`) so labels, bottle tops, and packaging text are 100% visible.</li>
                              <li><strong>Hover State:</strong> {productImages[1] ? 'Hovering over this card on the storefront will cross-fade to your second image.' : 'Add a 2nd image if you want customers to see an alternate view on hover.'}</li>
                              <li><strong>Catalog Alignment:</strong> All storefront cards maintain a strict `4:5` luxury aspect ratio to avoid jagged column heights.</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* ── SECTION 2: BASIC INFORMATION ── */}
                <div className="form-section">
                  <h3 className="section-title">2. Basic Information</h3>
                  <div className="form-grid">
                    <div className="form-group full-width">
                      <label>Product Name *</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="e.g. Allure Velvet Lip Tint"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label>Category *</label>
                      <input
                        type="text"
                        name="category"
                        value={formData.category}
                        onChange={handleInputChange}
                        placeholder="e.g. Lip Tint, Perfume, Bath and Body"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label>Brand</label>
                      <input
                        type="text"
                        name="brand"
                        value={formData.brand}
                        onChange={handleInputChange}
                        placeholder="AmaraCé"
                      />
                    </div>

                    <div className="form-group full-width">
                      <label>Short Description *</label>
                      <textarea
                        name="description"
                        rows="3"
                        value={formData.description}
                        onChange={handleInputChange}
                        placeholder="Provide a compelling description of this beauty product..."
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* ── SECTION 3: PRICING & INVENTORY ── */}
                <div className="form-section">
                  <h3 className="section-title">3. Pricing & Inventory Management</h3>
                  <div className="form-grid">
                    <div className="form-group">
                      <label>Selling Price (PHP) *</label>
                      <div className="input-prefix-box">
                        <span>₱</span>
                        <input
                          type="number"
                          name="price"
                          step="0.01"
                          min="0"
                          value={formData.price}
                          onChange={handleInputChange}
                          placeholder="399.00"
                          required
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label>Original Price / Compare At (PHP)</label>
                      <div className="input-prefix-box">
                        <span>₱</span>
                        <input
                          type="number"
                          name="originalPrice"
                          step="0.01"
                          min="0"
                          value={formData.originalPrice}
                          onChange={handleInputChange}
                          placeholder="499.00 (Optional strike-through)"
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label>SKU (Stock Keeping Unit)</label>
                      <input
                        type="text"
                        name="sku"
                        value={formData.sku}
                        onChange={handleInputChange}
                        placeholder="e.g. LT-ALLURE-01"
                      />
                    </div>

                    <div className="form-group">
                      <label>Current Stock Quantity *</label>
                      <input
                        type="number"
                        name="stock"
                        min="0"
                        value={formData.stock}
                        onChange={handleInputChange}
                        placeholder="25"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label>Low Stock Alert Threshold</label>
                      <input
                        type="number"
                        name="lowStockThreshold"
                        min="0"
                        value={formData.lowStockThreshold}
                        onChange={handleInputChange}
                        placeholder="10"
                      />
                      <small className="field-hint">Triggers Low Stock warning when stock is ≤ this number</small>
                    </div>

                    <div className="form-group">
                      <label>Calculated Status</label>
                      <div className="live-status-preview-box">
                        {(() => {
                          const s = getInventoryStatus(formData.stock, formData.lowStockThreshold);
                          return (
                            <span className={`status-pill ${s.class}`}>
                              {s.dot} {s.label}
                            </span>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                </div>

                {/* ── SECTION 4: VISIBILITY & MERCHANDISING ── */}
                <div className="form-section">
                  <h3 className="section-title">4. Visibility & Merchandising</h3>
                  
                  {/* Publication Toggle */}
                  <div className="form-group visibility-toggle-group">
                    <label className="toggle-switch-label">
                      <input
                        type="checkbox"
                        name="published"
                        checked={formData.published}
                        onChange={handleCheckboxChange}
                      />
                      <span className="toggle-slider" />
                      <div>
                        <strong>Published Status: {formData.published ? 'Published (Live)' : 'Draft (Hidden)'}</strong>
                        <p>{formData.published ? 'Visible to customers in the store catalogue' : 'Hidden from storefront searches and categories'}</p>
                      </div>
                    </label>
                  </div>

                  {/* Merchandising Checkboxes */}
                  <div className="merchandising-checkbox-grid">
                    <label className="checkbox-card">
                      <input
                        type="checkbox"
                        name="featured"
                        checked={formData.featured}
                        onChange={handleCheckboxChange}
                      />
                      <div className="cb-content">
                        <strong>★ Featured Product</strong>
                        <p>Displayed in Homepage Featured showcase</p>
                      </div>
                    </label>

                    <label className="checkbox-card">
                      <input
                        type="checkbox"
                        name="bestSeller"
                        checked={formData.bestSeller}
                        onChange={handleCheckboxChange}
                      />
                      <div className="cb-content">
                        <strong>🏆 Best Seller</strong>
                        <p>Highlights with gold Best Seller badge</p>
                      </div>
                    </label>

                    <label className="checkbox-card">
                      <input
                        type="checkbox"
                        name="newArrival"
                        checked={formData.newArrival}
                        onChange={handleCheckboxChange}
                      />
                      <div className="cb-content">
                        <strong>✨ New Arrival</strong>
                        <p>Highlights with New Arrival badge</p>
                      </div>
                    </label>
                  </div>
                </div>

                {/* ── SECTION 5: FORMULA & APPLICATION DETAILS ── */}
                <div className="form-section">
                  <h3 className="section-title">5. Formula & Application Ritual</h3>
                  <div className="form-grid">
                    <div className="form-group full-width">
                      <label>Ingredients (Bullet list or paragraph)</label>
                      <textarea
                        name="ingredients"
                        rows="3"
                        value={formData.ingredients}
                        onChange={handleInputChange}
                        placeholder="• Hyaluronic Acid • Vitamin E • Rosehip Seed Oil..."
                      />
                    </div>

                    <div className="form-group full-width">
                      <label>How to Use / Ritual Instructions</label>
                      <textarea
                        name="howToUse"
                        rows="3"
                        value={formData.howToUse}
                        onChange={handleInputChange}
                        placeholder="• Glide gently across lips starting from center outwards..."
                      />
                    </div>
                  </div>
                </div>

                {/* ── MODAL ACTIONS ── */}
                <div className="modal-actions-sticky">
                  <button
                    type="button"
                    className="btn-cancel-modal"
                    onClick={() => { setShowModal(false); setEditProduct(null); }}
                    disabled={saving}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-submit-modal"
                    disabled={saving}
                  >
                    {saving ? 'Saving Changes...' : editProduct ? 'Update Product' : 'Save & Publish Product'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ── Image Zoom Lightbox Overlay ───────────── */}
        {previewZoomImg && (
          <div className="admin-image-zoom-overlay" onClick={() => setPreviewZoomImg(null)}>
            <div className="admin-image-zoom-dialog" onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                className="zoom-close-btn"
                onClick={() => setPreviewZoomImg(null)}
                aria-label="Close zoom preview"
              >
                <FiX size={20} />
              </button>
              <div className="zoom-stage-view">
                <img src={previewZoomImg} alt="Enlarged view" />
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminProducts;